import React from 'react';
import {
	Box,
	Flex,
	HStack,
	Text,
	Avatar,
	Icon,
	IconButton,
	Menu,
	MenuButton,
	MenuList,
	MenuItem,
	MenuDivider,
	MenuGroup,
	Badge,
} from '@chakra-ui/react';
import CustomTooltip from 'components/shared/CustomTooltip';
import {
	FiArrowLeft,
	FiInfo,
	FiMoreVertical,
	FiBookmark,
	FiArchive,
	FiTag,
	FiTrash2,
	FiCheck,
} from 'react-icons/fi';
import { formatWaNumber, conversationTitle } from './phone';
import { MenuPortal, useMenuStrategy } from './portalTarget';
import { relative } from './time';
import WindowChip from './WindowChip';
import { usePrefs, prefs, LABELS } from '../hooks/useLocalPrefs';
import { useTyping } from '../hooks/useTyping';
import useAgentName from '../hooks/useAgentName';

/**
 * The thread header: who, and what is happening right now.
 *
 * The status line is honest about what this integration knows. WhatsApp shows
 * "online" and "typing…" for the other person; the Cloud API exposes neither
 * for a customer, so the line says when they last wrote — the thing that
 * actually decides whether the agent can reply (the 24-hour window). The only
 * live signal is another AGENT typing in this thread, and it is labelled as
 * such.
 *
 * `onBack` appears only on small screens where the list gave way to the
 * thread; the lead drawer passes neither `onBack` nor `onOpenInfo`, so those
 * buttons simply do not exist there.
 */
export default function ChatHeader({ conversation, onBack, onOpenInfo }) {
	const strategy = useMenuStrategy();
	const name = conversationTitle(conversation);
	const typing = useTyping(conversation?._id);
	const typistName = useAgentName(typing?.userId, conversation);
	const p = usePrefs();
	const id = conversation?._id;
	const pinned = Boolean(p.pinned[id]);
	const archived = Boolean(p.archived[id]);
	const labels = p.labels[id] ?? [];
	const hasDraft = Boolean(p.drafts[id]);
	const [, setTick] = React.useState(0);

	// "12 min ago" has to move on its own; a minute is plenty.
	React.useEffect(() => {
		const t = setInterval(() => setTick((n) => n + 1), 60000);
		return () => clearInterval(t);
	}, []);

	let status;
	if (typing) {
		status = (
			<Text fontSize="xs" color="wa.accent" noOfLines={1}>
				{typistName} is typing…
			</Text>
		);
	} else if (conversation?.lastInboundAt) {
		status = (
			<Text fontSize="xs" color="wa.inkMuted" noOfLines={1}>
				Customer last wrote {relative(conversation.lastInboundAt)}
			</Text>
		);
	} else {
		status = (
			<Text fontSize="xs" color="wa.inkMuted" noOfLines={1}>
				{formatWaNumber(conversation?.customerPhone || conversation?.waId)}
				{conversation?.channel?.label ? ` · ${conversation.channel.label}` : ''}
			</Text>
		);
	}

	return (
		<Flex px="2" py="2" bg="wa.header" borderBottomWidth="1px" borderColor="wa.divider" align="center">
			{onBack && (
				<IconButton
					aria-label="Back to chats"
					icon={<Icon as={FiArrowLeft} boxSize="5" />}
					variant="ghost"
					size="sm"
					borderRadius="full"
					color="wa.ink"
					onClick={onBack}
					mr="1"
				/>
			)}
			<Box
				as="button"
				type="button"
				display="flex"
				alignItems="center"
				minW="0"
				flex="1"
				textAlign="left"
				borderRadius="md"
				px="2"
				py="1"
				_hover={{ bg: onOpenInfo ? 'wa.hover' : 'transparent' }}
				onClick={onOpenInfo}
				aria-label={onOpenInfo ? 'Open contact info' : undefined}
				cursor={onOpenInfo ? 'pointer' : 'default'}
			>
				<Avatar w="40px" h="40px" name={name} mr="3" bg="wa.chip" color="wa.inkMuted" />
				<Box minW="0" flex="1">
					<HStack spacing="1.5">
						<Text fontSize="md" fontWeight="semibold" color="wa.ink" noOfLines={1}>
							{name}
						</Text>
						{pinned && <Icon as={FiBookmark} boxSize="14px" color="wa.inkMuted" />}
					</HStack>
					{status}
				</Box>
			</Box>

			<HStack spacing="1" ml="2" flexShrink={0}>
				<WindowChip lastInboundAt={conversation?.lastInboundAt} />
				{onOpenInfo && (
					<CustomTooltip label="Contact info" placement="bottom" hasArrow>
						<IconButton
							aria-label="Contact info"
							icon={<Icon as={FiInfo} boxSize="24px" />}
							variant="ghost"
							size="md"
							borderRadius="full"
							color="wa.ink"
							_hover={{ bg: 'wa.hover' }}
							onClick={onOpenInfo}
						/>
					</CustomTooltip>
				)}
				<Menu placement="bottom-end" strategy={strategy}>
					<MenuButton
						as={IconButton}
						aria-label="Chat options"
						icon={<Icon as={FiMoreVertical} boxSize="24px" />}
						variant="ghost"
						size="md"
						borderRadius="full"
						color="wa.ink"
						_hover={{ bg: 'wa.hover' }}
					/>
					<MenuPortal>
						<MenuList zIndex="popover" bg="wa.menu" borderColor="wa.divider" minW="220px" boxShadow="lg" color="wa.ink">
							{onOpenInfo && (
								<MenuItem icon={<Icon as={FiInfo} />} onClick={onOpenInfo} _hover={{ bg: 'wa.hover' }} _focus={{ bg: 'wa.hover' }}>
									Contact info
								</MenuItem>
							)}
							<MenuItem
								icon={<Icon as={FiBookmark} />}
								onClick={() => prefs.togglePin(id)}
								title="Kept on this device"
								_hover={{ bg: 'wa.hover' }}
								_focus={{ bg: 'wa.hover' }}
							>
								{pinned ? 'Unpin chat' : 'Pin chat'}
							</MenuItem>
							<MenuItem
								icon={<Icon as={FiArchive} />}
								onClick={() => prefs.toggleArchive(id)}
								title="Kept on this device"
								_hover={{ bg: 'wa.hover' }}
								_focus={{ bg: 'wa.hover' }}
							>
								{archived ? 'Unarchive chat' : 'Archive chat'}
							</MenuItem>
							<MenuDivider />
							<MenuGroup title="Labels (this device)" fontSize="xs" color="wa.inkMuted">
								{LABELS.map((l) => {
									const on = labels.includes(l.id);
									return (
										<MenuItem
											key={l.id}
											icon={<Icon as={on ? FiCheck : FiTag} color={on ? 'wa.accent' : undefined} />}
											onClick={() => prefs.toggleLabel(id, l.id)}
											_hover={{ bg: 'wa.hover' }}
											_focus={{ bg: 'wa.hover' }}
										>
											<Badge colorScheme={l.scheme} variant="subtle" fontSize="0.6rem" textTransform="none">
												{l.name}
											</Badge>
										</MenuItem>
									);
								})}
							</MenuGroup>
							{hasDraft && (
								<>
									<MenuDivider />
									<MenuItem
										icon={<Icon as={FiTrash2} />}
										onClick={() => prefs.setDraft(id, '')}
										_hover={{ bg: 'wa.hover' }}
										_focus={{ bg: 'wa.hover' }}
									>
										Discard draft
									</MenuItem>
								</>
							)}
						</MenuList>
					</MenuPortal>
				</Menu>
			</HStack>
		</Flex>
	);
}
